import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
    FormGroup,
    FormControl,
    ControlLabel,
    Button,
    Alert
} from "react-bootstrap";

class ExperienceForm extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            title: '',
            content: '',
            lesson: '',
            images: [],
            errors: {},
            isSubmitting: false
        };
    }

    handleInputChange = (field, value) => {
        this.setState({
            [field]: value,
            errors: {
                ...this.state.errors,
                [field]: null // Clear error when user starts typing
            }
        });
    };

    handleImageUpload = (e) => {
        const files = Array.from(e.target.files);
        const maxFiles = 3; // Réduit à 3 images max pour limiter la longueur
        const maxSize = 3 * 1024 * 1024; // 3MB

        if (files.length > maxFiles) {
            this.setState({
                errors: {
                    ...this.state.errors,
                    images: `Vous pouvez ajouter maximum ${maxFiles} images`
                }
            });
            return;
        }

        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            // Vérifier le type de fichier
            if (!file.type.startsWith('image/')) {
                errors.push(`${file.name} n'est pas une image valide`);
                return;
            }

            // Vérifier la taille
            if (file.size > maxSize) {
                errors.push(`${file.name} est trop volumineux (max 3MB)`);
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            this.setState({
                errors: {
                    ...this.state.errors,
                    images: errors.join(', ')
                }
            });
            return;
        }

        // Convertir les fichiers en URLs base64 pour l'aperçu et le stockage
        const imagePromises = validFiles.map(file => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => resolve({
                    file: file,
                    url: e.target.result, // URL base64 complète
                    name: file.name,
                    size: file.size,
                    type: file.type
                });
                reader.readAsDataURL(file);
            });
        });

        Promise.all(imagePromises).then(images => {
            this.setState({
                images: [...this.state.images, ...images],
                errors: {
                    ...this.state.errors,
                    images: null
                }
            });
        });
    };

    removeImage = (index) => {
        const newImages = this.state.images.filter((_, i) => i !== index);
        this.setState({ images: newImages });
    };

    validateForm = () => {
        const errors = {};
        const { title, content, lesson } = this.state;

        // Validation du titre
        if (!title.trim()) {
            errors.title = 'Le titre est obligatoire';
        } else if (title.trim().length < 10) {
            errors.title = 'Le titre doit être plus descriptif (min. 10 caractères)';
        } else if (title.trim().length > 80) {
            errors.title = 'Le titre doit rester concis (max. 80 caractères)';
        }

        // Validation du contenu
        if (!content.trim()) {
            errors.content = 'Le récit de votre expérience est obligatoire';
        } else if (content.trim().length < 50) {
            errors.content = 'Décrivez votre expérience de manière plus détaillée (min. 50 caractères)';
        } else if (content.trim().length > 1200) {
            errors.content = 'Le contenu doit rester concis - 1 page maximum (max. 1200 caractères)';
        }

        // Validation de la leçon (obligatoire)
        if (!lesson.trim()) {
            errors.lesson = 'La leçon à retenir est obligatoire';
        } else if (lesson.trim().length < 20) {
            errors.lesson = 'Détaillez davantage la leçon apprise (min. 20 caractères)';
        } else if (lesson.trim().length > 300) {
            errors.lesson = 'La leçon doit être concise (max. 300 caractères)';
        }

        // Vérification de l'anonymat dans le contenu
        const anonymityCheck = this.checkAnonymity(title + ' ' + content + ' ' + lesson);
        if (anonymityCheck.hasIssue) {
            errors.anonymity = anonymityCheck.message;
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    checkAnonymity = (text) => {
        const lowerText = text.toLowerCase();

        // Mots-clés potentiellement problématiques
        const personalIdentifiers = [
            'docteur', 'dr ', 'professeur', 'prof ', 'madame', 'monsieur', 'mme ', 'mr ',
            'patient ', 'patiente', 'collègue', 'infirmière', 'infirmier',
            'hôpital ', 'clinique ', 'centre médical', 'chu ', 'chr '
        ];

        const foundIdentifiers = personalIdentifiers.filter(identifier =>
            lowerText.includes(identifier)
        );

        if (foundIdentifiers.length > 0) {
            return {
                hasIssue: true,
                message: `Veillez à l'anonymat : évitez les mentions directes de personnes ou établissements. Utilisez "un professionnel", "un patient", "un établissement" etc.`
            };
        }

        return { hasIssue: false };
    };

    handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!this.validateForm()) {
            return;
        }

        this.setState({ isSubmitting: true });

        try {
            const { title, content, lesson, images } = this.state;

            // Utiliser les URLs base64 pour stocker les images
            const imageUrls = images.map(img => img.url);

            const experienceData = {
                title: title.trim(),
                content: content.trim(),
                lesson: lesson.trim(),
                images: imageUrls
            };

            await this.props.onSubmit(experienceData);
            
            // Reset form
            this.setState({
                title: '',
                content: '',
                lesson: '',
                images: [],
                errors: {}
            });

        } catch (error) {
            this.setState({
                errors: {
                    submit: 'Une erreur est survenue lors de la publication. Veuillez réessayer.'
                }
            });
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    render() {
        const { title, content, lesson, images, errors, isSubmitting } = this.state;
        const { onCancel } = this.props;

        return (
            <div className="modern-experience-form">
                <form onSubmit={this.handleSubmit}>

                    {/* Titre */}
                    <FormGroup validationState={errors.title ? 'error' : null}>
                        <ControlLabel>
                            Titre de votre expérience *
                        </ControlLabel>
                        <FormControl
                            type="text"
                            placeholder="Ex: Gestion d'une réaction allergique inattendue"
                            value={title}
                            onChange={(e) => this.handleInputChange('title', e.target.value)}
                            maxLength={80}
                            className="modern-input"
                        />
                        {errors.title && (
                            <span className="help-block error-text">{errors.title}</span>
                        )}
                        <span className="help-block char-counter">
                            {title.length}/80 caractères
                        </span>
                    </FormGroup>

                    {/* Contenu principal */}
                    <FormGroup validationState={errors.content ? 'error' : null}>
                        <ControlLabel>
                            Votre expérience (format libre) *
                        </ControlLabel>
                        <FormControl
                            componentClass="textarea"
                            rows={8}
                            placeholder="Décrivez votre expérience : le contexte, ce qui s'est passé, comment vous avez réagi, les résultats obtenus... Soyez naturel dans votre expression, mais gardez l'anonymat."
                            value={content}
                            onChange={(e) => this.handleInputChange('content', e.target.value)}
                            maxLength={1200}
                            className="modern-textarea"
                        />
                        {errors.content && (
                            <span className="help-block error-text">{errors.content}</span>
                        )}
                        <span className="help-block char-counter">
                            {content.length}/1200 caractères
                        </span>
                    </FormGroup>

                    {/* Leçon apprise */}
                    <FormGroup validationState={errors.lesson ? 'error' : null}>
                        <ControlLabel>
                            Leçon à retenir *
                        </ControlLabel>
                        <FormControl
                            componentClass="textarea"
                            rows={4}
                            placeholder="Quelle est la principale leçon que vous avez tirée de cette expérience ? Quels conseils donneriez-vous à un collègue dans une situation similaire ?"
                            value={lesson}
                            onChange={(e) => this.handleInputChange('lesson', e.target.value)}
                            maxLength={300}
                            className="modern-textarea lesson-input"
                        />
                        {errors.lesson && (
                            <span className="help-block error-text">{errors.lesson}</span>
                        )}
                        <span className="help-block char-counter">
                            {lesson.length}/300 caractères
                        </span>
                    </FormGroup>

                    {/* Upload d'images (optionnel) */}
                    <FormGroup validationState={errors.images ? 'error' : null}>
                        <ControlLabel>
                            <span className="field-icon">📸</span>
                            Images d'illustration (optionnel)
                        </ControlLabel>
                        <FormControl
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={this.handleImageUpload}
                            className="modern-file-input"
                        />
                        {errors.images && (
                            <span className="help-block error-text">{errors.images}</span>
                        )}
                        <span className="help-block">
                            Formats acceptés : JPG, PNG, GIF. Taille max : 3MB par image. Maximum 3 images.
                        </span>

                        {/* Aperçu des images */}
                        {images.length > 0 && (
                            <div className="image-preview">
                                <strong>Aperçu :</strong>
                                <div className="image-grid">
                                    {images.map((image, index) => (
                                        <div key={index} className="image-item">
                                            <img
                                                src={image.url}
                                                alt={`Aperçu ${index + 1}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => this.removeImage(index)}
                                                className="remove-image-btn"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </FormGroup>

                    {/* Alerte anonymat */}
                    {errors.anonymity && (
                        <Alert bsStyle="warning" className="anonymity-alert">
                            <strong>⚠️ Attention à l'anonymat :</strong> {errors.anonymity}
                        </Alert>
                    )}

                    {/* Erreur de soumission */}
                    {errors.submit && (
                        <Alert bsStyle="danger">
                            {errors.submit}
                        </Alert>
                    )}

                    {/* Boutons */}
                    <div className="form-actions">
                        <Button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            className="cancel-btn"
                        >
                            Annuler
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="submit-btn"
                        >
                            {isSubmitting ? (
                                <>
                                    <i className="fa fa-spinner fa-spin" />
                                    Publication...
                                </>
                            ) : (
                                <>
                                    <span className="btn-icon ">+</span>
                                    Publier l'expérience
                                </>
                            )}
                        </Button>
                    </div>
                </form>

                <style jsx>{`
                    .modern-experience-form {
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    }

                    .form-guidelines {
                        background: linear-gradient(135deg, #e6fffa 0%, #f0fff4 100%);
                        border: 2px solid #81e6d9;
                        border-radius: 15px;
                        padding: 30px;
                        margin-bottom: 35px;
                    }

                    .form-guidelines h4 {
                        color: #234e52;
                        margin: 0 0 20px 0;
                        font-size: 1.4rem;
                        font-weight: 700;
                    }

                    .form-guidelines ul {
                        margin: 0;
                        padding-left: 0;
                        list-style: none;
                    }

                    .form-guidelines li {
                        margin-bottom: 12px;
                        color: #2d5aa0;
                        font-size: 1.1rem;
                        line-height: 1.6;
                        display: flex;
                        align-items: flex-start;
                        gap: 10px;
                    }

                    .field-icon {
                        font-size: 1.4rem;
                        margin-right: 10px;
                    }

                    :global(.modern-input), 
                    :global(.modern-textarea) {
                        border-radius: 12px !important;
                        border: 2px solid #e2e8f0 !important;
                        padding: 22px !important;
                        font-size: 1.2rem !important;
                        transition: all 0.3s ease !important;
                        background: #fafbfc !important;
                        line-height: 1.6 !important;
                    }

                    :global(.modern-input:focus), 
                    :global(.modern-textarea:focus) {
                        border-color: #42a5f5 !important;
                        box-shadow: 0 0 0 3px rgba(66, 165, 245, 0.15) !important;
                        background: white !important;
                    }

                    :global(.lesson-input) {
                        background: linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%) !important;
                        border-color: #fbbf24 !important;
                        font-size: 1.2rem !important;
                    }

                    :global(.lesson-input:focus) {
                        border-color: #f59e0b !important;
                        box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15) !important;
                    }

                    :global(.modern-file-input) {
                        border-radius: 12px !important;
                        padding: 15px !important;
                        border: 2px dashed #cbd5e0 !important;
                        background: #f8fafc !important;
                        font-size: 1rem !important;
                    }

                    :global(.form-group label) {
                        font-size: 1.2rem !important;
                        font-weight: 600 !important;
                        color: #2d3748 !important;
                        margin-bottom: 8px !important;
                        display: flex !important;
                        align-items: center !important;
                    }

                    .char-counter {
                        font-size: 1rem;
                        color: #64748b;
                        float: right;
                        font-weight: 500;
                    }

                    .error-text {
                        color: #dc2626 !important;
                        font-weight: 600;
                        font-size: 1rem !important;
                    }

                    :global(.help-block) {
                        font-size: 1rem !important;
                        line-height: 1.5 !important;
                    }

                    .image-preview {
                        margin-top: 20px;
                        padding: 20px;
                        background: #f8fafc;
                        border-radius: 12px;
                        border: 1px solid #e2e8f0;
                    }

                    .image-grid {
                        display: flex;
                        gap: 15px;
                        margin-top: 15px;
                        flex-wrap: wrap;
                    }

                    .image-item {
                        position: relative;
                        width: 100px;
                        height: 100px;
                        border-radius: 12px;
                        overflow: hidden;
                        border: 2px solid #e2e8f0;
                    }

                    .image-item img {
                        width: 100%;
                        height: 100%;
                        object-fit: cover;
                    }

                    .remove-image-btn {
                        position: absolute;
                        top: 5px;
                        right: 5px;
                        background: rgba(239, 68, 68, 0.9);
                        color: white;
                        border: none;
                        border-radius: 50%;
                        width: 24px;
                        height: 24px;
                        cursor: pointer;
                        font-size: 14px;
                        font-weight: bold;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                    }

                    .remove-image-btn:hover {
                        background: #dc2626;
                        transform: scale(1.1);
                    }

                    .anonymity-alert {
                        border-radius: 12px !important;
                        border: 2px solid #fbbf24 !important;
                        background: #fefbf2 !important;
                    }

                    .form-actions {
                        display: flex;
                        justify-content: flex-end;
                        gap: 20px;
                        margin-top: 45px;
                        padding-top: 30px;
                        border-top: 2px solid #f1f5f9;
                    }

                    .cancel-btn {
                        background: transparent !important;
                        border: 2px solid #e2e8f0 !important;
                        color: #64748b !important;
                        padding: 15px 30px !important;
                        border-radius: 25px !important;
                        font-weight: 600 !important;
                        font-size: 1.1rem !important;
                        transition: all 0.3s ease !important;
                    }

                    .cancel-btn:hover {
                        border-color: #cbd5e0 !important;
                        color: #475569 !important;
                        transform: translateY(-2px) !important;
                    }

                    .submit-btn {
                        background: linear-gradient(135deg, #42a5f5 0%, #2196f3 100%) !important;
                        border: none !important;
                        color: white !important;
                        padding: 15px 35px !important;
                        border-radius: 25px !important;
                        font-weight: 700 !important;
                        font-size: 1.2rem !important;
                        display: flex !important;
                        align-items: center !important;
                        gap: 12px !important;
                        transition: all 0.3s ease !important;
                    }

                    .submit-btn:hover {
                        transform: translateY(-3px) !important;
                        box-shadow: 0 10px 25px rgba(66, 165, 245, 0.4) !important;
                        background: linear-gradient(135deg, #2196f3 0%, #42a5f5 100%) !important;
                    }

                    .btn-icon {
                        font-size: 1.4rem;
                        animation: bounce 2s infinite;
                    }

                    @keyframes bounce {
                        0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                        40% { transform: translateY(-3px); }
                        60% { transform: translateY(-2px); }
                    }

                    @media (max-width: 768px) {
                        .form-actions {
                            flex-direction: column;
                        }

                        .cancel-btn, .submit-btn {
                            width: 100% !important;
                            justify-content: center !important;
                        }

                        .image-grid {
                            justify-content: center;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

ExperienceForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default ExperienceForm;